// useOrgModules — fetches the current user's enabled module list
// from GET /api/modules/my and caches it in localStorage.
//
// Skips the fetch entirely for superadmins (no org) and for
// unauthenticated visitors. Refreshes every 5 minutes.
//
// Returns { modules, isEnabled, loading } where:
//   - modules:   { core_hr: true, leave: true, ... }
//   - isEnabled: (key) => boolean (defaults to true while loading
//                or for unknown keys, so the UI doesn't flash empty)
//   - loading:   true during the first fetch only

import { useEffect, useMemo, useState } from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const CACHE_KEY = "org_modules";
const CACHE_TS_KEY = "org_modules_ts";
const REFRESH_MS = 5 * 60 * 1000;  // 5 minutes

function readCachedMap() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedMap(map) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(map));
    localStorage.setItem(CACHE_TS_KEY, String(Date.now()));
  } catch {}
}

function readCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function modulesArrayToMap(arr) {
  const out = {};
  if (!Array.isArray(arr)) return out;
  for (const m of arr) {
    if (m && typeof m.module_key === "string") {
      out[m.module_key] = !!m.is_enabled;
    }
  }
  return out;
}

export default function useOrgModules() {
  const cached = readCachedMap();
  const [modules, setModules] = useState(cached || {});
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    const user = readCurrentUser();
    const token = localStorage.getItem("token");

    // Skip fetch entirely for superadmins, unauthenticated, or no-org users.
    if (!user || !token || user.role === "superadmin") {
      setLoading(false);
      return;
    }

    let cancelled = false;
    let timer = null;

    const fetchOnce = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/modules/my`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          // Don't clobber cache on transient errors
          if (cancelled) return;
          setLoading(false);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        const map = modulesArrayToMap(data?.modules);
        setModules(map);
        writeCachedMap(map);
      } catch {
        // network error — keep current state
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOnce();
    timer = setInterval(fetchOnce, REFRESH_MS);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, []);

  const isEnabled = useMemo(() => {
    return (key) => {
      // Default to true so UI doesn't blank out for unknown / pre-fetch states.
      if (modules[key] === undefined) return true;
      return !!modules[key];
    };
  }, [modules]);

  return { modules, isEnabled, loading };
}
